import { brandAssets } from '@echo/brand';
import logo from '@echo/brand/assets/logo-lockup-goodwin.png';
import {
  IdentifierInUseError,
  isProfileComplete,
  maskIdentifier,
  missingIdentifier,
  ONEARLY_PRIVACY_URL,
  readCoreErrorCode,
  TERMS_URL,
} from '@echo/core';
import {
  type IdentifierVerification,
  useCompleteProfile,
  useMe,
  useSession,
} from '@echo/core/react';
import { Button, TextField } from '@echo/ui-web';
import { Trans, useLingui } from '@lingui/react/macro';
import { createFileRoute, Navigate } from '@tanstack/react-router';
import { type FormEvent, useState } from 'react';
import { OtpField } from '@/components/otp-field';
import { PhoneField, type PhoneValue } from '@/components/phone-field';

export const Route = createFileRoute('/onboarding')({ component: OnboardingPage });

const CODE_LENGTH = 6;

/**
 * "Complete your profile" — the only onboarding step for now
 * (docs/mvp/02-ONBOARDING.md). `_app.tsx` sends people here while the OnEarly profile lacks
 * a name; the identifier they signed in with is enough and the other one is optional.
 * Once the names are saved the page sends them back to Home.
 */
function OnboardingPage() {
  const session = useSession();
  const me = useMe();
  if (session.status === 'unknown' || (session.status === 'authenticated' && !me.isFetched)) {
    return null;
  }
  if (session.status !== 'authenticated') return <Navigate to="/sign-in" replace />;
  if (!me.data || isProfileComplete(me.data)) return <Navigate to="/" replace />;
  return <CompleteProfileForm profile={me.data} />;
}

function CompleteProfileForm({
  profile: current,
}: {
  profile: {
    firstName: string;
    lastName: string | null;
    email: string | null;
    phoneNumber: string | null;
  };
}) {
  const { t } = useLingui();
  const profile = useCompleteProfile();
  const missing = missingIdentifier(current);
  const [firstName, setFirstName] = useState(current.firstName);
  const [lastName, setLastName] = useState(current.lastName ?? '');
  const [phone, setPhone] = useState<PhoneValue>({ e164: '', valid: false });
  const [email, setEmail] = useState('');
  const [verification, setVerification] = useState<IdentifierVerification | undefined>();
  const [code, setCode] = useState('');
  const [attempt, setAttempt] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const namesReady = firstName.trim().length > 0 && lastName.trim().length > 0;
  // The second identifier is optional: empty is fine, anything typed has to be valid.
  const secondIdentifier =
    missing === 'phone' ? phone.e164 : missing === 'email' ? email.trim() : '';
  const identifierReady =
    secondIdentifier === '' ||
    (missing === 'phone' ? phone.valid : missing === 'email' ? email.includes('@') : true);
  const sentTo = verification ? maskIdentifier(verification.channel, verification.identifier) : '';

  function describe(e: unknown): string {
    if (e instanceof IdentifierInUseError) {
      return e.channel === 'phone'
        ? t`That phone number already belongs to another account. Use a different one.`
        : t`That email already belongs to another account. Use a different one.`;
    }
    switch (readCoreErrorCode(e)) {
      case 'INVALID_CODE':
        return t`That code isn't right. Check it and try again.`;
      case 'FLOW_EXPIRED':
        return t`That code has expired. Ask for a new one.`;
      default:
        return t`Something went wrong`;
    }
  }

  async function run(task: () => Promise<void>) {
    setBusy(true);
    setError(undefined);
    try {
      await task();
    } catch (e) {
      setError(describe(e));
    } finally {
      setBusy(false);
    }
  }

  const clearCodeForRetry = () => {
    setCode('');
    setAttempt((n) => n + 1);
  };

  const verify = (entered = code) =>
    run(async () => {
      if (!verification || entered.length !== CODE_LENGTH) return;
      try {
        await profile.verify.mutateAsync({ verification, code: entered });
      } catch (e) {
        clearCodeForRetry();
        throw e;
      }
    });

  /** Pasted or autofilled codes verify on their own; a typed sixth digit waits for Continue. */
  const onCodeChange = (next: string) => {
    const arrivedAtOnce =
      next.length - code.length > 1 ||
      (code.length === CODE_LENGTH && next.length === CODE_LENGTH && next !== code);
    setCode(next);
    if (next.length === CODE_LENGTH && arrivedAtOnce) void verify(next);
  };

  function submit(event: FormEvent) {
    event.preventDefault();
    void run(async () => {
      if (verification) {
        await verify();
        return;
      }
      await profile.saveNames.mutateAsync({ firstName, lastName });
      if (missing && secondIdentifier) {
        setVerification(
          await profile.startVerification.mutateAsync({
            channel: missing,
            identifier: secondIdentifier,
          }),
        );
        return;
      }
      await profile.finish.mutateAsync();
    });
  }

  // On the code step: keep the account as it is (the names are saved) and go to Home.
  const skip = () => run(() => profile.finish.mutateAsync());

  const resend = () =>
    run(async () => {
      if (!verification) return;
      setVerification(await profile.resend.mutateAsync(verification));
      clearCodeForRetry();
    });

  const changeContact = () => {
    setVerification(undefined);
    setCode('');
    setError(undefined);
  };

  return (
    <div className="min-h-dvh bg-surface">
      <main className="mx-auto flex w-full max-w-[480px] flex-col gap-content-lg px-content-md pt-section-sm pb-content-lg">
        <img
          src={logo}
          alt="Stronger Memory, powered by Goodwin Living Foundation"
          width={brandAssets.logoLockupGoodwin.width}
          height={brandAssets.logoLockupGoodwin.height}
          className="h-auto w-[180px]"
        />
        <h1 className="text-headline-lg">
          {verification ? (
            <Trans>Confirm your details</Trans>
          ) : (
            <Trans>Complete your profile</Trans>
          )}
        </h1>
        {verification ? (
          <p className="text-body-md">
            <Trans>
              We’ve sent a 6-digit code to
              <br />
              <strong>{sentTo}</strong>
            </Trans>
          </p>
        ) : null}
        <form onSubmit={submit} className="flex flex-col gap-content-md">
          {verification ? (
            <>
              <div className="flex flex-col gap-internal-md">
                <p className="text-label-sm">
                  <Trans>Enter the code to continue.</Trans>
                </p>
                <OtpField
                  key={attempt}
                  value={code}
                  onChange={onCodeChange}
                  disabled={busy}
                  accessibilityLabel={t`6-digit verification code`}
                />
                <p className="text-body-xs text-on-surface-weak">
                  <Trans>Didn't get the code?</Trans>{' '}
                  <button
                    type="button"
                    onClick={resend}
                    disabled={busy}
                    className="cursor-pointer text-link-onearly underline"
                  >
                    <Trans>Resend code</Trans>
                  </button>
                </p>
              </div>
              <button
                type="button"
                onClick={changeContact}
                disabled={busy}
                className="self-start cursor-pointer text-body-sm text-link-onearly underline"
              >
                <Trans>Change phone number or email</Trans>
              </button>
              <div className="flex flex-col gap-content-sm">
                <Button
                  type="submit"
                  loading={busy}
                  variant="onearly"
                  width="full"
                  disabled={busy || code.length !== CODE_LENGTH}
                >
                  <Trans>Continue</Trans>
                </Button>
                <Button type="button" variant="ghost" width="full" onClick={skip} disabled={busy}>
                  <Trans>Skip for now</Trans>
                </Button>
              </div>
            </>
          ) : (
            <>
              <TextField
                label={t`First name`}
                value={firstName}
                onChange={(e) => setFirstName(e.currentTarget.value)}
                autoComplete="given-name"
                placeholder={t({ message: 'Jane', comment: 'Example first name in the input' })}
                autoFocus
              />
              <TextField
                label={t`Last name`}
                value={lastName}
                onChange={(e) => setLastName(e.currentTarget.value)}
                autoComplete="family-name"
                placeholder={t({ message: 'Doe', comment: 'Example last name in the input' })}
              />
              {/* The identifier they signed in with is read-only; the other one is optional. */}
              {missing !== 'email' ? (
                <div className="flex flex-col gap-internal-sm">
                  <span className="font-brand text-label-sm text-on-surface">
                    <Trans>Email</Trans>
                  </span>
                  <p className="text-body-md text-on-surface-weak">{current.email ?? ''}</p>
                </div>
              ) : (
                <TextField
                  label={t`Email`}
                  indicator={t`(optional)`}
                  value={email}
                  onChange={(e) => setEmail(e.currentTarget.value)}
                  type="email"
                  autoComplete="email"
                  placeholder={t({
                    message: 'you@example.com',
                    comment: 'Example email address in the input',
                  })}
                />
              )}
              {missing === 'phone' ? (
                <PhoneField
                  label={t`Phone number`}
                  indicator={t`(optional)`}
                  onChange={setPhone}
                  disabled={busy}
                />
              ) : (
                <div className="flex flex-col gap-internal-sm">
                  <span className="font-brand text-label-sm text-on-surface">
                    <Trans>Phone number</Trans>
                  </span>
                  <p className="text-body-md text-on-surface-weak">{current.phoneNumber ?? ''}</p>
                </div>
              )}
              <div className="flex flex-col gap-content-sm">
                <p className="text-body-xs text-on-surface-weak">
                  <Trans>
                    By continuing you agree to the{' '}
                    <a href={TERMS_URL} className="text-link-onearly underline">
                      Terms
                    </a>{' '}
                    &{' '}
                    <a href={ONEARLY_PRIVACY_URL} className="text-link-onearly underline">
                      Privacy Policy
                    </a>
                    .
                  </Trans>
                </p>
                <Button
                  type="submit"
                  loading={busy}
                  variant="onearly"
                  width="full"
                  disabled={busy || !namesReady || !identifierReady}
                >
                  <Trans>Continue</Trans>
                </Button>
              </div>
            </>
          )}
        </form>
        {error ? (
          <p role="alert" className="text-body-sm text-status-error">
            {error}
          </p>
        ) : null}
      </main>
    </div>
  );
}
